/**
 * VendorFormDialog — ultra-detailed New/Edit Vendor form.
 * Sections: Identity · Address · Contacts · Categories · Commercial ·
 * Certifications · Banking · Ratings · Compliance · Audit.
 * All dropdowns fed from `/mci/vendor-po/lookups`.
 */
import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  X, Save, Plus, Trash2, Loader2, Building2, MapPin, Users, Award, Banknote,
  Star, Shield, FileText, ChevronDown, ChevronRight,
} from 'lucide-react';

const now = () => new Date().toLocaleString();
const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
  catch { return {}; }
};

const emptyContact = () => ({ role: 'PRIMARY', name: '', email: '', phone: '', whatsapp: '', designation: '' });
const emptyAddress = kind => ({ kind, line1: '', line2: '', city: '', state: '', country: '', postalCode: '' });

const EMPTY_VENDOR = {
  legalName: '', tradeName: '', taxId: '', crNumber: '', website: '',
  country: '', foundedYear: '', employeeCount: '', annualRevenueUsd: '',
  addresses: [emptyAddress('HQ')],
  contacts: [emptyContact()],
  categories: [], disciplines: [], areasOfSupply: [], certifications: [], certDocumentRefs: [],
  banking: { beneficiary: '', bankName: '', accountNumber: '', iban: '', swift: '', currency: '' },
  defaultCurrency: '', defaultIncoterms: '', defaultPaymentTerms: '', creditLimitUsd: '',
  rating: { qa: '', delivery: '', commercial: '' },
  status: 'PROVISIONAL', approvedSince: '', ndaOnFile: false, notes: '',
};

function Section({ icon: Icon, title, open, onToggle, children, testId }) {
  return (
    <div className="rounded-lg border" style={{ borderColor: 'var(--border-color)' }} data-testid={testId}>
      <button type="button" onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-2 text-left"
        style={{ background: 'var(--card-bg-alt)' }}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Icon className="w-4 h-4" style={{ color: 'var(--tone-navy-text)' }} />
        <span className="text-[11px] uppercase font-bold tracking-wider" style={{ color: 'var(--text-primary)' }}>{title}</span>
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

const inp = "w-full px-2 py-1.5 rounded border text-xs";
const inpSty = { background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' };
const lbl = "text-[9px] uppercase font-semibold block mb-1 tracking-wider";
const lblSty = { color: 'var(--text-muted)' };

function ChipMultiSelect({ options, value, onChange, testId }) {
  const toggle = (opt) => {
    const has = value.includes(opt);
    onChange(has ? value.filter(v => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {options.map(opt => {
        const on = value.includes(opt);
        return (
          <button key={opt} type="button" onClick={() => toggle(opt)}
            className="px-2 py-0.5 rounded-full text-[10px] font-semibold border"
            style={{
              background: on ? '#1E4E8C' : 'var(--card-bg)',
              color: on ? '#fff' : 'var(--text-primary)',
              borderColor: on ? '#1E4E8C' : 'var(--border-color)',
            }}>{opt}</button>
        );
      })}
    </div>
  );
}

export default function VendorFormDialog({ projectId, vendor, onClose, onSaved }) {
  const editing = !!vendor?.id;
  const [form, setForm] = useState(vendor ? { ...EMPTY_VENDOR, ...vendor } : EMPTY_VENDOR);
  const [lookups, setLookups] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({ identity: true, addr: true, cats: true, contacts: true, comm: true, cert: false, bank: false, rate: false, comp: false });
  const user = useMemo(currentUser, []);

  useEffect(() => {
    let alive = true;
    api.get(`/v1/projects/${projectId}/mci/vendor-po/lookups`)
      .then(r => { if (alive) setLookups(r.data); })
      .catch(() => toast.error('Failed to load lookups'));
    return () => { alive = false; };
  }, [projectId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setNested = (k, subK, v) => setForm(f => ({ ...f, [k]: { ...(f[k] || {}), [subK]: v } }));
  const setContact = (i, k, v) => setForm(f => ({
    ...f, contacts: f.contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c)
  }));
  const addContact = () => setForm(f => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const rmContact = i => setForm(f => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));
  const setAddress = (i, k, v) => setForm(f => ({
    ...f, addresses: f.addresses.map((a, idx) => idx === i ? { ...a, [k]: v } : a)
  }));
  const addAddress = kind => setForm(f => ({ ...f, addresses: [...f.addresses, emptyAddress(kind)] }));
  const rmAddress = i => setForm(f => ({ ...f, addresses: f.addresses.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.legalName.trim()) return toast.error('Legal name is required');
    setSaving(true);
    try {
      const clean = { ...form };
      ['foundedYear', 'employeeCount'].forEach(k => { clean[k] = clean[k] ? parseInt(clean[k]) : null; });
      ['annualRevenueUsd', 'creditLimitUsd'].forEach(k => { clean[k] = clean[k] ? parseFloat(clean[k]) : null; });
      if (clean.rating) {
        ['qa', 'delivery', 'commercial'].forEach(k => {
          clean.rating[k] = clean.rating[k] ? parseFloat(clean.rating[k]) : null;
        });
      }
      if (editing) {
        await api.put(`/v1/projects/${projectId}/mci/vendors/${vendor.id}`, clean);
        toast.success(`Vendor '${clean.legalName}' updated`);
      } else {
        await api.post(`/v1/projects/${projectId}/mci/vendors`, clean);
        toast.success(`Vendor '${clean.legalName}' created`);
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const L = lookups || { countries: [], currencies: [], incoterms: [], paymentTerms: [],
                          certifications: [], contactRoles: [], vendorStatuses: [],
                          disciplines: [], areas: [], materialCategories: ['BULK', 'TAGGED'] };
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose} data-testid="vendor-form-dialog">
      <div className="w-full max-w-3xl h-[90vh] rounded-xl shadow-2xl flex flex-col"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" style={{ color: '#F26B1F' }} />
            <div>
              <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                {editing ? `Edit Vendor · ${vendor.legalName}` : 'New Vendor Master Record'}
              </div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {editing ? 'Updated' : 'Raised'} by <b>{user.name || user.email || 'current user'}</b> · {now()}
              </div>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <Section icon={Building2} title="Identity" open={open.identity} onToggle={() => toggle('identity')} testId="section-identity">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className={lbl} style={lblSty}>Legal Name *</label>
                <input value={form.legalName} onChange={e => set('legalName', e.target.value)}
                  className={inp} style={inpSty} data-testid="v-legal-name" required />
              </div>
              <div><label className={lbl} style={lblSty}>Trade Name</label>
                <input value={form.tradeName || ''} onChange={e => set('tradeName', e.target.value)} className={inp} style={inpSty} data-testid="v-trade-name" /></div>
              <div><label className={lbl} style={lblSty}>Website</label>
                <input value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://…" className={inp} style={inpSty} data-testid="v-website" /></div>
              <div><label className={lbl} style={lblSty}>Tax ID / VAT / GSTIN</label>
                <input value={form.taxId || ''} onChange={e => set('taxId', e.target.value)} className={inp} style={inpSty} data-testid="v-tax-id" /></div>
              <div><label className={lbl} style={lblSty}>Commercial Reg #</label>
                <input value={form.crNumber || ''} onChange={e => set('crNumber', e.target.value)} className={inp} style={inpSty} data-testid="v-cr-number" /></div>
              <div><label className={lbl} style={lblSty}>Country of Incorporation</label>
                <select value={form.country || ''} onChange={e => set('country', e.target.value)} className={inp} style={inpSty} data-testid="v-country">
                  <option value="">— pick —</option>
                  {L.countries.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Founded Year</label>
                <input type="number" value={form.foundedYear || ''} onChange={e => set('foundedYear', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Employees</label>
                <input type="number" value={form.employeeCount || ''} onChange={e => set('employeeCount', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Annual Revenue (USD)</label>
                <input type="number" value={form.annualRevenueUsd || ''} onChange={e => set('annualRevenueUsd', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={MapPin} title="Addresses" open={open.addr} onToggle={() => toggle('addr')} testId="section-addr">
            {(form.addresses || []).map((a, i) => (
              <div key={i} className="p-2 rounded border grid grid-cols-4 gap-2" style={{ borderColor: 'var(--border-color)' }}>
                <div><label className={lbl} style={lblSty}>Kind</label>
                  <select value={a.kind} onChange={e => setAddress(i, 'kind', e.target.value)} className={inp} style={inpSty}>
                    <option>HQ</option><option>BILLING</option><option>SHIPPING</option>
                  </select></div>
                <div className="col-span-3"><label className={lbl} style={lblSty}>Line 1</label>
                  <input value={a.line1 || ''} onChange={e => setAddress(i, 'line1', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>City</label>
                  <input value={a.city || ''} onChange={e => setAddress(i, 'city', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>State</label>
                  <input value={a.state || ''} onChange={e => setAddress(i, 'state', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>Country</label>
                  <select value={a.country || ''} onChange={e => setAddress(i, 'country', e.target.value)} className={inp} style={inpSty}>
                    <option value="">— pick —</option>
                    {L.countries.map(c => <option key={c}>{c}</option>)}
                  </select></div>
                <div><label className={lbl} style={lblSty}>Postal</label>
                  <div className="flex gap-1">
                    <input value={a.postalCode || ''} onChange={e => setAddress(i, 'postalCode', e.target.value)} className={inp} style={inpSty} />
                    <button type="button" onClick={() => rmAddress(i)} className="px-1.5 rounded border" style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div></div>
              </div>
            ))}
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => addAddress('BILLING')}><Plus className="w-3 h-3 mr-1" /> Billing</Button>
              <Button size="sm" variant="outline" onClick={() => addAddress('SHIPPING')}><Plus className="w-3 h-3 mr-1" /> Shipping</Button>
            </div>
          </Section>

          <Section icon={Award} title="Categories · Disciplines · Areas" open={open.cats} onToggle={() => toggle('cats')} testId="section-cats">
            <div><label className={lbl} style={lblSty}>Material Categories</label>
              <ChipMultiSelect options={L.materialCategories} value={form.categories} onChange={v => set('categories', v)} testId="v-categories" /></div>
            <div><label className={lbl} style={lblSty}>Disciplines</label>
              <ChipMultiSelect options={L.disciplines.length ? L.disciplines : ['Piping','Mechanical','Electrical','Instrumentation','Civil','Structural']} value={form.disciplines} onChange={v => set('disciplines', v)} testId="v-disciplines" /></div>
            <div><label className={lbl} style={lblSty}>Areas of Supply</label>
              <input value={(form.areasOfSupply || []).join(', ')} onChange={e => set('areasOfSupply', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Comma-separated" className={inp} style={inpSty} /></div>
          </Section>

          <Section icon={Users} title={`Contacts (${form.contacts.length})`} open={open.contacts} onToggle={() => toggle('contacts')} testId="section-contacts">
            {form.contacts.map((c, i) => (
              <div key={i} className="p-2 rounded border grid grid-cols-6 gap-2" style={{ borderColor: 'var(--border-color)' }} data-testid={`contact-row-${i}`}>
                <div><label className={lbl} style={lblSty}>Role</label>
                  <select value={c.role} onChange={e => setContact(i, 'role', e.target.value)} className={inp} style={inpSty}>
                    {L.contactRoles.map(r => <option key={r}>{r}</option>)}
                  </select></div>
                <div className="col-span-2"><label className={lbl} style={lblSty}>Name</label>
                  <input value={c.name} onChange={e => setContact(i, 'name', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>Designation</label>
                  <input value={c.designation || ''} onChange={e => setContact(i, 'designation', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>Email</label>
                  <input value={c.email || ''} onChange={e => setContact(i, 'email', e.target.value)} className={inp} style={inpSty} /></div>
                <div><label className={lbl} style={lblSty}>Phone</label>
                  <div className="flex gap-1">
                    <input value={c.phone || ''} onChange={e => setContact(i, 'phone', e.target.value)} className={inp} style={inpSty} />
                    <button type="button" onClick={() => rmContact(i)} className="px-1.5 rounded border" style={{ borderColor: '#EF4444', color: '#EF4444' }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div></div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addContact} data-testid="add-contact-btn"><Plus className="w-3 h-3 mr-1" /> Add contact</Button>
          </Section>

          <Section icon={FileText} title="Commercial Defaults" open={open.comm} onToggle={() => toggle('comm')} testId="section-comm">
            <div className="grid grid-cols-4 gap-2">
              <div><label className={lbl} style={lblSty}>Currency</label>
                <select value={form.defaultCurrency || ''} onChange={e => set('defaultCurrency', e.target.value)} className={inp} style={inpSty} data-testid="v-currency">
                  <option value="">—</option>
                  {L.currencies.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Incoterms</label>
                <select value={form.defaultIncoterms || ''} onChange={e => set('defaultIncoterms', e.target.value)} className={inp} style={inpSty}>
                  <option value="">—</option>
                  {L.incoterms.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div className="col-span-2"><label className={lbl} style={lblSty}>Payment Terms</label>
                <select value={form.defaultPaymentTerms || ''} onChange={e => set('defaultPaymentTerms', e.target.value)} className={inp} style={inpSty}>
                  <option value="">—</option>
                  {L.paymentTerms.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Credit Limit (USD)</label>
                <input type="number" value={form.creditLimitUsd || ''} onChange={e => set('creditLimitUsd', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={Award} title="Certifications" open={open.cert} onToggle={() => toggle('cert')} testId="section-cert">
            <ChipMultiSelect options={L.certifications} value={form.certifications} onChange={v => set('certifications', v)} testId="v-certifications" />
            <div><label className={lbl} style={lblSty}>Certificate Document URLs (comma-separated)</label>
              <input value={(form.certDocumentRefs || []).join(', ')} onChange={e => set('certDocumentRefs', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className={inp} style={inpSty} /></div>
          </Section>

          <Section icon={Banknote} title="Banking" open={open.bank} onToggle={() => toggle('bank')} testId="section-bank">
            <div className="grid grid-cols-3 gap-2">
              <div><label className={lbl} style={lblSty}>Beneficiary</label>
                <input value={form.banking?.beneficiary || ''} onChange={e => setNested('banking', 'beneficiary', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Bank Name</label>
                <input value={form.banking?.bankName || ''} onChange={e => setNested('banking', 'bankName', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Currency</label>
                <select value={form.banking?.currency || ''} onChange={e => setNested('banking', 'currency', e.target.value)} className={inp} style={inpSty}>
                  <option value="">—</option>{L.currencies.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Account #</label>
                <input value={form.banking?.accountNumber || ''} onChange={e => setNested('banking', 'accountNumber', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>IBAN</label>
                <input value={form.banking?.iban || ''} onChange={e => setNested('banking', 'iban', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>SWIFT</label>
                <input value={form.banking?.swift || ''} onChange={e => setNested('banking', 'swift', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={Star} title="Performance Ratings (1-5)" open={open.rate} onToggle={() => toggle('rate')} testId="section-rate">
            <div className="grid grid-cols-3 gap-2">
              <div><label className={lbl} style={lblSty}>QA</label>
                <input type="number" min="1" max="5" step="0.1" value={form.rating?.qa || ''} onChange={e => setNested('rating', 'qa', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Delivery</label>
                <input type="number" min="1" max="5" step="0.1" value={form.rating?.delivery || ''} onChange={e => setNested('rating', 'delivery', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Commercial</label>
                <input type="number" min="1" max="5" step="0.1" value={form.rating?.commercial || ''} onChange={e => setNested('rating', 'commercial', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={Shield} title="Compliance & Status" open={open.comp} onToggle={() => toggle('comp')} testId="section-comp">
            <div className="grid grid-cols-3 gap-2">
              <div><label className={lbl} style={lblSty}>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)} className={inp} style={inpSty} data-testid="v-status">
                  {L.vendorStatuses.map(s => <option key={s}>{s}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Approved Since</label>
                <input type="date" value={form.approvedSince || ''} onChange={e => set('approvedSince', e.target.value)} className={inp} style={inpSty} /></div>
              <label className="flex items-center gap-2 text-[11px] mt-4" style={{ color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={!!form.ndaOnFile} onChange={e => set('ndaOnFile', e.target.checked)} />
                NDA on file
              </label>
            </div>
            <div><label className={lbl} style={lblSty}>Notes</label>
              <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className={inp} style={inpSty} /></div>
          </Section>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-end gap-2 sticky bottom-0"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="v-save"
            className="text-white font-semibold" style={{ background: '#F26B1F' }}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {editing ? 'Update Vendor' : 'Create Vendor'}
          </Button>
        </div>
      </div>
    </div>
  );
}
